import React, { useState, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table, TableHeader, TableRow, TableHead,
  TableBody, TableCell
} from '@/components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue
} from '@/components/ui/select';
import { EmailCustomer } from '@/hooks/email/types';
import {
  Search, UserCircle, Phone, Mail, Info,
  ArrowUpDown, ChevronLeft, ChevronRight, AlertCircle, AlertTriangle
} from 'lucide-react';
import { UserRole } from '@/context/auth/types';
import { getDaysSinceOrder } from '@/components/email/subscribers/utils';
import { formatLastOrderDate } from '@/utils/date-helpers';

interface EmailSubscribersListProps {
  subscribers: EmailCustomer[];
  onSubscriberSelect: (subscriber: EmailCustomer) => void;
  onRefresh: () => void;
  userRole?: UserRole;
}

type SortField = 'name' | 'status' | 'lastOrder' | 'totalOrders' | 'totalSpend';
type SortOrder = 'asc' | 'desc';

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const EmailSubscribersList: React.FC<EmailSubscribersListProps> = ({
  subscribers,
  onSubscriberSelect,
  userRole
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const canModifySubscribers = useMemo(() => {
    return userRole === UserRole.SUPER_ADMIN ||
          userRole === UserRole.ORG_ADMIN ||
          userRole === UserRole.REST_ADMIN;
  }, [userRole]);

  // Apply filters and sorting
  const filteredSubscribers = useMemo(() => {
    if (!subscribers) return [];

    return subscribers
      .filter(subscriber => {
        // Status filter
        if (statusFilter !== 'all' && subscriber.status !== statusFilter) {
          return false;
        }

        // Search filter
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          return (
            (subscriber.name && subscriber.name.toLowerCase().includes(query)) ||
            (subscriber.email && subscriber.email.toLowerCase().includes(query)) ||
            (subscriber.phone && subscriber.phone.includes(query))
          );
        }

        return true;
      })
      .sort((a, b) => {
        // Apply sorting
        const multiplier = sortOrder === 'asc' ? 1 : -1;

        switch (sortField) {
          case 'name':
            return multiplier * (a.name || '').localeCompare(b.name || '');
          case 'status':
            return multiplier * (a.status || '').localeCompare(b.status || '');
          case 'lastOrder':
            return multiplier * (getDaysSinceOrder(a.lastOrder || '') - getDaysSinceOrder(b.lastOrder || ''));
          case 'totalOrders':
            return multiplier * ((a.totalOrders || 0) - (b.totalOrders || 0));
          case 'totalSpend':
            const spendA = typeof a.totalSpend === 'string' ? parseFloat(a.totalSpend) : (a.totalSpend || 0);
            const spendB = typeof b.totalSpend === 'string' ? parseFloat(b.totalSpend) : (b.totalSpend || 0);
            return multiplier * (spendA - spendB);
          default:
            return 0;
        }
      });
  }, [subscribers, searchQuery, statusFilter, sortField, sortOrder]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredSubscribers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, filteredSubscribers.length);
  const paginatedSubscribers = filteredSubscribers.slice(startIndex, endIndex);

  // Handle sort toggle
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Status badge color
  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'subscribed': return 'bg-ubereats-green hover:bg-ubereats-green/90 text-brand-white';
      case 'unsubscribed': return 'bg-brand-yellow hover:bg-brand-yellow/90 text-brand-dark-text';
      case 'bounced': return 'bg-brand-red hover:bg-brand-red/90 text-brand-white';
      case 'complained': return 'bg-brand-red hover:bg-brand-red/90 text-brand-white';
      case 'not_subscribed': return 'bg-brand-grey hover:bg-brand-grey/90 text-brand-dark-text';
      default: return 'bg-brand-grey hover:bg-brand-grey/90 text-brand-dark-text';
    }
  };

  // Format status text
  const formatStatus = (status?: string) => {
    switch (status) {
      case 'subscribed': return 'Subscribed';
      case 'unsubscribed': return 'Unsubscribed';
      case 'bounced': return 'Bounced';
      case 'complained': return 'Complained';
      case 'not_subscribed': return 'Not Subscribed';
      default: return 'Unknown';
    }
  };

  if (!subscribers || subscribers.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col items-center text-center py-8">
            <UserCircle className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No Subscribers Found</h3>
            <p className="text-muted-foreground mb-4">
              There are no email subscribers matching your criteria.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter and search controls */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email or phone..."
            className="pl-9"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1); // Reset to first page on search
            }}
          />
        </div>

        <div className="flex gap-4">
          <Select
            value={statusFilter}
            onValueChange={(value) => {
              setStatusFilter(value);
              setCurrentPage(1); // Reset to first page on filter change
            }}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="subscribed">Subscribed</SelectItem>
              <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
              <SelectItem value="bounced">Bounced</SelectItem>
              <SelectItem value="complained">Complained</SelectItem>
              <SelectItem value="not_subscribed">Not Subscribed</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={String(itemsPerPage)}
            onValueChange={(value) => {
              setItemsPerPage(Number(value));
              setCurrentPage(1); // Reset to first page on items per page change
            }}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="Per page" />
            </SelectTrigger>
            <SelectContent>
              {ITEMS_PER_PAGE_OPTIONS.map(option => (
                <SelectItem key={option} value={String(option)}>
                  {option} rows
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead onClick={() => handleSort('name')} className="cursor-pointer">
                    <div className="flex items-center">
                      Subscriber
                      {sortField === 'name' && (
                        <ArrowUpDown className={`ml-1 h-4 w-4 ${sortOrder === 'desc' ? 'rotate-180' : ''} transition-transform`} />
                      )}
                    </div>
                  </TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead onClick={() => handleSort('status')} className="cursor-pointer">
                    <div className="flex items-center">
                      Status
                      {sortField === 'status' && (
                        <ArrowUpDown className={`ml-1 h-4 w-4 ${sortOrder === 'desc' ? 'rotate-180' : ''} transition-transform`} />
                      )}
                    </div>
                  </TableHead>
                  <TableHead onClick={() => handleSort('totalOrders')} className="cursor-pointer">
                    <div className="flex items-center">
                      Orders
                      {sortField === 'totalOrders' && (
                        <ArrowUpDown className={`ml-1 h-4 w-4 ${sortOrder === 'desc' ? 'rotate-180' : ''} transition-transform`} />
                      )}
                    </div>
                  </TableHead>
                  <TableHead onClick={() => handleSort('lastOrder')} className="cursor-pointer">
                    <div className="flex items-center">
                      Last Order
                      {sortField === 'lastOrder' && (
                        <ArrowUpDown className={`ml-1 h-4 w-4 ${sortOrder === 'desc' ? 'rotate-180' : ''} transition-transform`} />
                      )}
                    </div>
                  </TableHead>
                  <TableHead onClick={() => handleSort('totalSpend')} className="cursor-pointer text-right">
                    <div className="flex items-center justify-end">
                      Total Spend
                      {sortField === 'totalSpend' && (
                        <ArrowUpDown className={`ml-1 h-4 w-4 ${sortOrder === 'desc' ? 'rotate-180' : ''} transition-transform`} />
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedSubscribers.map((subscriber) => (
                  <TableRow key={subscriber.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarImage
                            src={`https://api.dicebear.com/7.x/initials/svg?seed=${subscriber.name || subscriber.email}`}
                          />
                          <AvatarFallback>
                            <UserCircle className="h-5 w-5" />
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{subscriber.name || 'Unknown'}</p>
                          {subscriber.phone && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {subscriber.phone}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Mail className="h-3 w-3 text-muted-foreground" />
                        {subscriber.email || 'No email'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1 items-start">
                        <Badge
                          variant={subscriber.status === 'subscribed' ? "default" : "secondary"}
                          className={getStatusColor(subscriber.status)}
                        >
                          {formatStatus(subscriber.status)}
                        </Badge>
                        {subscriber.has_hard_bounce && (
                          <Badge variant="destructive" className="text-xs">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Bounced
                          </Badge>
                        )}
                        {subscriber.has_complaint && (
                          <Badge variant="destructive" className="text-xs">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Complaint
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{subscriber.totalOrders || 0}</TableCell>
                    <TableCell>{formatLastOrderDate(subscriber.lastOrder)}</TableCell>
                    <TableCell className="text-right">
                      ${typeof subscriber.totalSpend === 'number'
                        ? subscriber.totalSpend.toFixed(2)
                        : parseFloat(subscriber.totalSpend || '0').toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => onSubscriberSelect(subscriber)}
                        >
                          <Info className="h-4 w-4" />
                          <span className="sr-only">View details</span>
                        </Button>
                        {canModifySubscribers && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                          >
                            <Mail className="h-4 w-4" />
                            <span className="sr-only">Send email</span>
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-2 mt-4">
              <p className="text-sm text-muted-foreground">
                Showing {startIndex + 1}-{endIndex} of {filteredSubscribers.length} subscribers
              </p>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="h-8 w-8 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span className="sr-only">Previous</span>
                </Button>

                <div className="text-sm">
                  Page {currentPage} of {totalPages}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="h-8 w-8 p-0"
                >
                  <ChevronRight className="h-4 w-4" />
                  <span className="sr-only">Next</span>
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Analytics summary cards */}
      <div className="grid gap-4 md:grid-cols-4 mt-2">
        <Card>
          <CardContent className="p-4 text-center">
            <h4 className="text-xl font-bold mb-1">{filteredSubscribers.length}</h4>
            <p className="text-sm text-muted-foreground">Matching Subscribers</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <h4 className="text-xl font-bold mb-1">
              {filteredSubscribers.filter(sub => sub.status === 'subscribed').length}
            </h4>
            <p className="text-sm text-muted-foreground">Active Subscribers</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <h4 className="text-xl font-bold mb-1">
              {filteredSubscribers.filter(sub => sub.has_hard_bounce).length}
            </h4>
            <p className="text-sm text-muted-foreground">Hard Bounces</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <h4 className="text-xl font-bold mb-1">
              {filteredSubscribers.length ?
                Math.round((filteredSubscribers.filter(sub => sub.status === 'subscribed').length / filteredSubscribers.length) * 100) : 0}%
            </h4>
            <p className="text-sm text-muted-foreground">Opt-in Rate</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmailSubscribersList;
